import { Box, TablePagination } from '@mui/material';
import type { GridApi } from '@mui/x-data-grid-premium';
import { DEFAULT_CURRENCY, type CustomerRevenueReport, type RevenuePerMonthBreakdown, type YearlyRevenue } from '@vy/types';
import { caseInsensitiveIncludes, entries, isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import { useCallback, useMemo, useState } from 'react';
import { VyTableWaterfallPremium } from '../../../../../components/new-design/table/vy-table/VyWaterfallTablePremium';
import { useTreeData } from '../../../../../hooks/use-tree-data';
import { TOTALS_ROW_ID } from '../../../reports-components/report-table-consts';
import {
  CUSTOMER_REVENUE_REPORT_KEYS,
  useCustomerRevenueReportColumns,
} from './CustomerRevenueReportTableColumns';
import {
  aggregateYearlyRevenue,
  expandCustomerRevenueReportItems,
  ROW_LEVELS,
  type ExpandedCustomerRevenueReportRow,
} from './customer-revenue-report-row-expander';
import { computeWaterfallTotals } from './compute-waterfall-totals';
import { ProductInspectDialog } from '../../../../../components/ProductInspectDialog';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 500];
const DEFAULT_PAGE_SIZE = 100;
const PINNED_COLUMNS = {
  left: [CUSTOMER_REVENUE_REPORT_KEYS.EXPAND, CUSTOMER_REVENUE_REPORT_KEYS.CUSTOMER_NAME],
  right: [CUSTOMER_REVENUE_REPORT_KEYS.RECOGNIZED, CUSTOMER_REVENUE_REPORT_KEYS.REMAINING],
};

type CustomerRevenueReportTableProps = {
  report: CustomerRevenueReport;
  filter?: string;
  fromDate?: Date | null;
  recognizedUpTo?: Date | null;
  apiRef?: React.MutableRefObject<GridApi>;
  isLoading?: boolean;
};

export const CustomerRevenueReportTable: React.FC<CustomerRevenueReportTableProps> = ({
  report,
  filter,
  fromDate,
  recognizedUpTo,
  isLoading,
  apiRef,
}) => {
  const normalizedFilter = filter?.trim();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const expandedRows = useMemo(() => {
    return expandCustomerRevenueReportItems(report.reportItems);
  }, [report.reportItems]);

  const treeRows = useTreeData(expandedRows);

  const filteredRows = useMemo(() => {
    if (isNullOrUndefined(normalizedFilter) || normalizedFilter === '') {
      return treeRows;
    }

    const matchingCustomerIds = new Set<string>();

    for (const item of treeRows) {
      if (caseInsensitiveIncludes(item.customer.name, normalizedFilter)) {
        matchingCustomerIds.add(item.customer.id);
      }

      if (
        isNotNullOrUndefined(item.product?.name) &&
        caseInsensitiveIncludes(item.product.name, normalizedFilter)
      ) {
        matchingCustomerIds.add(item.customer.id);
      }

      if (
        isNotNullOrUndefined(item.productVariant) &&
        caseInsensitiveIncludes(item.productVariant.name, normalizedFilter)
      ) {
        matchingCustomerIds.add(item.customer.id);
      }

      if (
        isNotNullOrUndefined(item.revisionedLineItem) &&
        caseInsensitiveIncludes(item.revisionedLineItem.name, normalizedFilter)
      ) {
        matchingCustomerIds.add(item.customer.id);
      }
    }

    return treeRows.filter((item) => {
      return matchingCustomerIds.has(item.customer.id);
    });
  }, [treeRows, normalizedFilter]);

  // Products are the top-level rows now (ENG-6239), so pagination counts
  // products; a page also carries each product's revenue-type children.
  const visibleRootRows = useMemo(
    () => filteredRows.filter((row) => row.level === ROW_LEVELS.Product),
    [filteredRows],
  );

  const totalPages = Math.ceil(visibleRootRows.length / pageSize);
  const effectivePage = Math.max(Math.min(page, totalPages - 1), 0);

  const paginatedRows = useMemo(() => {
    const startIndex = effectivePage * pageSize;
    const pageRoots = visibleRootRows.slice(startIndex, startIndex + pageSize);
    const pageRootIds = new Set(pageRoots.map((r) => r.id));

    const rowMap = new Map(filteredRows.map((r) => [r.id, r]));

    return filteredRows.filter((row) => {
      if (row.level === ROW_LEVELS.Product) {
        return pageRootIds.has(row.id);
      }

      let currentParentId = row.parentId;
      while (isNotNullOrUndefined(currentParentId)) {
        if (pageRootIds.has(currentParentId)) {
          return true;
        }
        currentParentId = rowMap.get(currentParentId)?.parentId;
      }

      return false;
    });
  }, [filteredRows, visibleRootRows, effectivePage, pageSize]);

  const [editingRow, setEditingRow] = useState<ExpandedCustomerRevenueReportRow | null>(null);

  const handleOpenEditDialog = useCallback((row: ExpandedCustomerRevenueReportRow) => {
    setEditingRow(row);
  }, []);

  const handleCloseEditDialog = useCallback(() => {
    setEditingRow(null);
  }, []);

  const columns = useCustomerRevenueReportColumns(
    report.reportItems,
    fromDate,
    recognizedUpTo,
    handleOpenEditDialog,
  );

  const [isTotalsExpanded, setIsTotalsExpanded] = useState(false);

  const toggleTotalsExpand = useCallback(() => {
    setIsTotalsExpanded((prev) => !prev);
  }, []);

  const searchFilteredReportItems = useMemo(() => {
    if (isNullOrUndefined(normalizedFilter) || normalizedFilter === '') {
      return report.reportItems;
    }

    return report.reportItems.filter((item) => {
      if (caseInsensitiveIncludes(item.customer.name, normalizedFilter)) return true;
      if (isNotNullOrUndefined(item.product?.name) && caseInsensitiveIncludes(item.product.name, normalizedFilter)) return true;
      if (isNotNullOrUndefined(item.productVariant) && caseInsensitiveIncludes(item.productVariant.name, normalizedFilter)) return true;
      if (isNotNullOrUndefined(item.revisionedLineItem) && caseInsensitiveIncludes(item.revisionedLineItem.name, normalizedFilter)) return true;
      return false;
    });
  }, [report.reportItems, normalizedFilter]);

  const aggregatedRevenue = useMemo(
    () => aggregateYearlyRevenue(searchFilteredReportItems),
    [searchFilteredReportItems],
  );

  const computedTotals = useMemo(
    () => computeWaterfallTotals(searchFilteredReportItems),
    [searchFilteredReportItems],
  );

  const { waterfallRevenue, unbilledWaterfallRevenue } = useMemo(
    () => mapTotalsToYearlyRevenue(computedTotals),
    [computedTotals],
  );

  const totalsRow = useMemo((): ExpandedCustomerRevenueReportRow => {
    return {
      customer: { id: TOTALS_ROW_ID, name: 'Total', currency: DEFAULT_CURRENCY },
      yearlyRevenue: aggregatedRevenue,
      revenueType: 'total',
      level: ROW_LEVELS.Total,
      id: TOTALS_ROW_ID,
      isParent: true,
      isExpanded: isTotalsExpanded,
      toggleExpand: toggleTotalsExpand,
    };
  }, [aggregatedRevenue, isTotalsExpanded, toggleTotalsExpand]);

  const pinnedRows = useMemo(() => {
    const rows: ExpandedCustomerRevenueReportRow[] = [totalsRow];

    if (isTotalsExpanded) {
      rows.push(
        {
          customer: { id: TOTALS_ROW_ID, name: 'Total', currency: DEFAULT_CURRENCY },
          yearlyRevenue: {},
          revenueType: 'spacer',
          level: ROW_LEVELS.TotalChild,
          id: `${TOTALS_ROW_ID}spacer_before_deferred__`,
          isParent: false,
          isExpanded: false,
          isChild: true,
        },
        {
          customer: { id: TOTALS_ROW_ID, name: 'Total', currency: DEFAULT_CURRENCY },
          yearlyRevenue: waterfallRevenue,
          revenueType: 'newDeferred',
          level: ROW_LEVELS.TotalChild,
          id: `${TOTALS_ROW_ID}new_deferred__`,
          isParent: false,
          isExpanded: false,
          isChild: true,
        },
        {
          customer: { id: TOTALS_ROW_ID, name: 'Total', currency: DEFAULT_CURRENCY },
          yearlyRevenue: waterfallRevenue,
          revenueType: 'recognizedDeferred',
          level: ROW_LEVELS.TotalChild,
          id: `${TOTALS_ROW_ID}recognized_deferred__`,
          isParent: false,
          isExpanded: false,
          isChild: true,
        },
        {
          customer: { id: TOTALS_ROW_ID, name: 'Total', currency: DEFAULT_CURRENCY },
          yearlyRevenue: waterfallRevenue,
          revenueType: 'endingDeferred',
          level: ROW_LEVELS.TotalChild,
          id: `${TOTALS_ROW_ID}ending_deferred__`,
          isParent: false,
          isExpanded: false,
          isChild: true,
        },
        {
          customer: { id: TOTALS_ROW_ID, name: 'Total', currency: DEFAULT_CURRENCY },
          yearlyRevenue: {},
          revenueType: 'spacer',
          level: ROW_LEVELS.TotalChild,
          id: `${TOTALS_ROW_ID}spacer__`,
          isParent: false,
          isExpanded: false,
          isChild: true,
        },
        {
          customer: { id: TOTALS_ROW_ID, name: 'Total', currency: DEFAULT_CURRENCY },
          yearlyRevenue: unbilledWaterfallRevenue,
          revenueType: 'newUnbilledAR',
          level: ROW_LEVELS.TotalChild,
          id: `${TOTALS_ROW_ID}new_unbilled_ar__`,
          isParent: false,
          isExpanded: false,
          isChild: true,
        },
        {
          customer: { id: TOTALS_ROW_ID, name: 'Total', currency: DEFAULT_CURRENCY },
          yearlyRevenue: unbilledWaterfallRevenue,
          revenueType: 'recognizedUnbilled',
          level: ROW_LEVELS.TotalChild,
          id: `${TOTALS_ROW_ID}recognized_unbilled__`,
          isParent: false,
          isExpanded: false,
          isChild: true,
        },
        {
          customer: { id: TOTALS_ROW_ID, name: 'Total', currency: DEFAULT_CURRENCY },
          yearlyRevenue: unbilledWaterfallRevenue,
          revenueType: 'endingUnbilled',
          level: ROW_LEVELS.TotalChild,
          id: `${TOTALS_ROW_ID}ending_unbilled__`,
          isParent: false,
          isExpanded: false,
          isChild: true,
        },
      );
    }

    return { bottom: rows };
  }, [totalsRow, isTotalsExpanded, waterfallRevenue, unbilledWaterfallRevenue]);

  const getRowId = useCallback((row: ExpandedCustomerRevenueReportRow) => row.id, []);

  const getRowClassName = useCallback(
    (params: { row: ExpandedCustomerRevenueReportRow }) => (params.row.isChild ? 'vy-child-row' : ''),
    [],
  );

  const handlePageChange = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(Number(event.target.value));
    setPage(0);
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <VyTableWaterfallPremium
          apiRef={apiRef}
          columns={columns}
          items={paginatedRows}
          loadingItems={isLoading}
          getRowId={getRowId}
          getRowClassName={getRowClassName}
          pinnedColumns={PINNED_COLUMNS}
          pinnedRows={pinnedRows}
          hideFooter
          density="compact"
        />
      </Box>
      <TablePagination
        component="div"
        count={visibleRootRows.length}
        page={effectivePage}
        rowsPerPage={pageSize}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePageSizeChange}
        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        sx={{ borderTop: 1, borderColor: 'divider', flexShrink: 0 }}
      />
      <ProductInspectDialog row={editingRow} onClose={handleCloseEditDialog} />
    </Box>
  );
};

function mapTotalsToYearlyRevenue(totals: CustomerRevenueReport['totals']) {
  const empty = { waterfallRevenue: {} as YearlyRevenue, unbilledWaterfallRevenue: {} as YearlyRevenue };
  if (!totals) return empty;

  const deferred: Record<string, Record<string, RevenuePerMonthBreakdown>> = {};
  const unbilled: Record<string, Record<string, RevenuePerMonthBreakdown>> = {};

  for (const [year, months] of entries(totals)) {
    deferred[year] = {};
    unbilled[year] = {};

    for (const [month, breakdown] of entries(months)) {
      if (!breakdown) continue;

      deferred[year][month] = {
        scheduledCharge: breakdown.newDeferredRev,
        revenue: breakdown.recognizedRevenueDeferred,
        deferredRevenue: breakdown.endingDeferredBalance,
        unbilledRevenue: 0,
      };

      unbilled[year][month] = {
        scheduledCharge: breakdown.newUnbilledAR,
        revenue: breakdown.recognizedRevenueUnbilled,
        unbilledRevenue: breakdown.endingUnbilledAR,
        deferredRevenue: 0,
      };
    }
  }

  return {
    waterfallRevenue: deferred as YearlyRevenue,
    unbilledWaterfallRevenue: unbilled as YearlyRevenue,
  };
}
